import { Image, Box, Text, Divider } from '@chakra-ui/react';
import React, { useState } from 'react';
import MapSignPost from '../assets/images/MapSignPost.png';
import MapFromMenu from '../assets/images/MapFromMenu.png';
import { Entry } from '../Icons/Entry';
import { YellowPin } from '../Icons/YellowPin';
import { Neroli } from '../Icons/Neroli';
import { Close } from '../Icons/Close';
import { MapItem, MapProps } from '../interfaces/components';
import { RightArrow } from '../Icons/RightArrow';

interface MapSceneItemProps extends MapItem {
  activeLocale: string;
  hlookat?: string;
  vlookat?: string;
  close: () => void;
}

const MapSceneItem = ({ activeLocale, icon, text, bg, scene, hlookat, vlookat, close }: MapSceneItemProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Box
      borderRadius="8px"
      padding={['6px 8px 6px 8px']}
      position={'relative'}
      background={`linear-gradient(to right, ${bg}, rgba(0, 0, 255, 0))`}
      display="flex"
      alignItems="center"
      gap={[2]}
      cursor={'pointer'}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        window.GotoScene && window.GotoScene(scene, hlookat, vlookat);
        close();
      }}
    >
      {icon}
      <Text fontFamily={activeLocale === 'ar' ? 'GETwoMedium' : 'LoccitaneRegular'} fontSize={['16px']}>
        {activeLocale == 'ar' ? text.ar : text.en}
      </Text>
      {isHovered ? (
        <RightArrow
          width={'16px'}
          height={'16px'}
          position={'absolute'}
          top={'50%'}
          right={'10px'}
          transform={'translateY(-50%)'}
          transition={'all 0.3s'}
          stroke="black"
        />
      ) : (
        ''
      )}
    </Box>
  );
};

function Map({ activeLocale, active, close, mapType }: MapProps) {
  const provencalGarden: MapItem[] = [
    {
      id: 1,
      bg: 'rgba(241, 243, 193, 1)',
      icon: <Neroli boxSize={[6]} />,
      text: {
        en: 'Néroli & Orchidée',
        ar: 'نيرولي وأوركيد',
      },
      scene: 'loccitane_final_greenhouse_2',
    },
    {
      id: 2,
      bg: 'rgba(255, 174, 156, 1)',
      icon: <Neroli boxSize={[6]} />,
      text: {
        en: 'Rose',
        ar: 'الورد',
      },
      scene: 'loccitane_final_greenhouse_3',
    },
    {
      id: 3,
      bg: 'rgba(255, 231, 228, 1)',
      icon: <Neroli boxSize={[6]} />,
      text: {
        en: 'Cherry Blossom',
        ar: 'زهرة الكرز',
      },
      scene: 'loccitane_final_greenhouse_10',
    },
    {
      id: 4,
      bg: 'rgba(229, 214, 233, 1)',
      icon: <Neroli boxSize={[6]} />,
      text: {
        en: 'White Lavender',
        ar: 'اللافندر الأبيض',
      },
      scene: 'loccitane_final_greenhouse_12',
    },
    {
      id: 5,
      bg: 'rgba(217, 165, 255, 1)',
      icon: <Neroli boxSize={[6]} />,
      text: {
        en: 'Arlésienne',
        ar: 'آرليزيان',
      },
      scene: 'loccitane_final_greenhouse_9',
    },
    {
      id: 6,
      bg: 'rgba(255, 150, 197, 1)',
      icon: <Neroli boxSize={[6]} />,
      text: {
        en: 'Pivoine Flora',
        ar: 'بيفوان فلورا',
      },
      scene: 'loccitane_final_greenhouse_11',
    },
  ];

  const middleEastern: MapItem[] = [
    {
      id: 7,
      bg: 'rgba(249, 232, 218, 1)',
      icon: <Neroli boxSize={[6]} />,
      text: {
        en: '86 Intense',
        ar: '٨٦ إنتنس',
      },
      scene: 'loccitane_final_greenhouse_6',
    },
    {
      id: 8,
      bg: 'rgba(244, 191, 74, 0.5)',
      icon: <Neroli boxSize={[6]} />,
      text: {
        en: 'Ambre',
        ar: 'العنبر',
      },
      scene: 'loccitane_final_greenhouse_8',
    },
    {
      id: 9,
      bg: 'rgba(249, 177, 187, 1)',
      icon: <Neroli boxSize={[6]} />,
      text: {
        en: "Eau D'Iparie",
        ar: 'إيباري',
      },
      scene: 'loccitane_final_greenhouse_7',
    },
  ];

  const commonItems: MapItem[] = [
    {
      id: 10,
      bg: 'rgba(195, 160, 129, 1)',
      icon: <Neroli boxSize={[6]} />,
      text: {
        en: 'Men Collection',
        ar: 'للرجال',
      },
      scene: 'loccitane_final_greenhouse_14',
    },
    {
      id: 11,
      bg: 'rgba(232, 236, 164, 1)',
      icon: <Neroli boxSize={[6]} />,
      text: {
        en: 'Verbena',
        ar: 'فربينا',
      },
      scene: 'loccitane_final_greenhouse_14',
      hlookat: '-8260.386805603945',
      vlookat: '7.897686011035738',
    },
  ];

  return (
    <Box
      zIndex="10"
      opacity={active ? 1 : 0}
      visibility={active ? 'visible' : 'hidden'}
      transition="all 0.5s"
      position="fixed"
      top={0}
      right={0}
      left={0}
      bottom={0}
      margin="auto"
      height={['100%', '600px', '450px', '500px', '650px']}
      width={['100%', '360px', '700px', '850px', '950px']}
      backgroundColor={'white'}
      pointerEvents={active ? 'all' : 'none'}
      boxShadow="rgba(0, 0, 0, 0.35) 0px 5px 15px"
      padding={['20px', '20px', '22px', '24px', '24px']}
    >
      <Box
        width={['100%', '100%', '100%', '100%', '100%']}
        height={['100%', '100%', '100%', '100%', '100%']}
        display="flex"
        flexDirection={['column-reverse', 'column-reverse', 'row', 'row', 'row']}
        justifyContent="center"
        alignItems="center"
        outline={'4px solid #FFCB00'}
      >
        <Close
          boxSize={[4]}
          position="absolute"
          top={['30px']}
          right={['30px']}
          onClick={close}
          zIndex="99"
          cursor="pointer"
        />
        <Box
          height="100%"
          width={['100%', '100%', '35%', '30%', '30%']}
          padding={['10px', '10px', '15px', '20px', '20px']}
          display="flex"
          flexDirection="column"
          gap={[4]}
          overflow="auto"
          style={{
            scrollbarWidth: 'none',
          }}
        >
          <Box width={['100%']} height="auto" display="flex" flexDirection="column" gap={[4]}>
            <Box width={['100%']} display="flex" flexDirection="column" gap={[1]}>
              <Text fontFamily={activeLocale === 'ar' ? 'GETwoMedium' : 'RalewayRegular'} fontSize={['14px']}>
                Provençal Garden
              </Text>
              <Divider orientation="vertical" borderColor="rgba(204, 204, 204, 1)" border="1px" />
            </Box>
            <Box display="flex" flexDirection="column" gap={[1]}>
              {provencalGarden.map((provencalGardenItem: MapItem, index: number) => (
                <MapSceneItem
                  key={index}
                  id={provencalGardenItem.id}
                  activeLocale={activeLocale}
                  icon={provencalGardenItem.icon}
                  text={provencalGardenItem.text}
                  bg={provencalGardenItem.bg}
                  scene={provencalGardenItem.scene}
                  hlookat={provencalGardenItem.hlookat}
                  vlookat={provencalGardenItem.vlookat}
                  close={close}
                />
              ))}
            </Box>
          </Box>

          <Box width={['100%']} height="auto" display="flex" flexDirection="column" gap={[4]}>
            <Box width={['100%']} display="flex" flexDirection="column" gap={[1]}>
              <Text fontFamily={activeLocale === 'ar' ? 'GETwoMedium' : 'RalewayRegular'} fontSize={['14px']}>
                Middle Eastern Oasis
              </Text>
              <Divider orientation="vertical" borderColor="rgba(204, 204, 204, 1)" border="1px" />
            </Box>
            <Box display="flex" flexDirection="column" gap={[1]}>
              {middleEastern.map((provencalGardenItem: MapItem, index: number) => (
                <MapSceneItem
                  key={index}
                  id={provencalGardenItem.id}
                  activeLocale={activeLocale}
                  icon={provencalGardenItem.icon}
                  text={provencalGardenItem.text}
                  bg={provencalGardenItem.bg}
                  scene={provencalGardenItem.scene}
                  hlookat={provencalGardenItem.hlookat}
                  vlookat={provencalGardenItem.vlookat}
                  close={close}
                />
              ))}
            </Box>
          </Box>

          <Box width={['100%']} height="auto" display="flex" flexDirection="column" gap={[4]}>
            <Box width={['100%']} display="flex" flexDirection="column" gap={[1]}>
              <Divider orientation="vertical" borderColor="rgba(204, 204, 204, 1)" border="1px" />
            </Box>
            <Box display="flex" flexDirection="column" gap={[1]}>
              {commonItems.map((provencalGardenItem: MapItem, index: number) => (
                <MapSceneItem
                  key={index}
                  id={provencalGardenItem.id}
                  activeLocale={activeLocale}
                  icon={provencalGardenItem.icon}
                  text={provencalGardenItem.text}
                  bg={provencalGardenItem.bg}
                  scene={provencalGardenItem.scene}
                  hlookat={provencalGardenItem.hlookat}
                  vlookat={provencalGardenItem.vlookat}
                  close={close}
                />
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          height="100%"
          width={['100%', '100%', '65%', '70%', '70%']}
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="relative"
        >
          <Image src={mapType === 'overlay' ? MapFromMenu : MapSignPost} width={['95%', '95%', '95%', '95%', '95%']} />

          <Box
            position="absolute"
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems="flex-start"
            right={['20px', '20px', '70px', '70px', '70px']}
            bottom={['20px', '20px', '70px', '70px', '70px']}
            gap={[1, 1, 2, 2, 2]}
          >
            <Box display="flex" justifyContent="center" alignItems="center" gap={[1, 1, 2, 2, 2]}>
              <Entry boxSize={[4, 4, 6, 6, 6]} />
              <Text
                fontFamily={activeLocale === 'ar' ? 'GETwoMedium' : 'LoccitaneRegular'}
                fontSize={['12px', '12px', '14px', '16px', '16px']}
              >
                Entrance
              </Text>
            </Box>
            {mapType === 'signPost' && (
              <Box display="flex" justifyContent="center" alignItems="center" gap={[1, 1, 2, 2, 2]}>
                <YellowPin boxSize={[4, 4, 6, 6, 6]} />
                <Text
                  fontFamily={activeLocale === 'ar' ? 'GETwoMedium' : 'LoccitaneRegular'}
                  fontSize={['12px', '12px', '14px', '16px', '16px']}
                >
                  You are Here
                </Text>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Map;
